import { clsx } from 'clsx'
import { Link } from '@remix-run/react'
import { getLinkType } from '~/utils'
import type { SanityRefDeconstructed } from '~/sanity'

type ButtonStyles = 'gold' | 'whiteOutline'

export interface ButtonProps {
  title: string
  to?: string
  linkType?: string
  linkReference?: SanityRefDeconstructed
  linkSlug?: string
  linkExternal?: string
  disabled?: boolean
  style?: ButtonStyles
  mimic?: boolean
  size?: 'text-sm' | 'text-base' | 'text-lg' | 'text-xl' | 'text-2xl'
  onClick?: () => void
}

interface ButtonInnerProps {
  title: string
}

const ButtonInner = ({ title }: ButtonInnerProps) => (
  <span>
    <span className="inline-block">{title}</span>
  </span>
)

const Button = ({
  title,
  to,
  linkType,
  linkReference,
  linkSlug,
  linkExternal,
  size = 'text-base',
  style = 'gold',
  mimic = false,
  onClick,
}: ButtonProps) => {
  if (!title) return null

  const regex = new RegExp('^(http|https|mailto|tel)://', 'i')

  const classes = clsx(
    'border text-center hover:border-gold-300 hover:bg-gold-300 hover:text-white',
    size,
    {
      'border-transparent bg-gold-500 text-white': style === 'gold',
      'border-white text-white': style === 'whiteOutline',
      'p-3': size === 'text-sm',
      'px-8 py-3': size === 'text-base',
      'p-8': size === 'text-2xl',
    }
  )

  const link =
    to ?? getLinkType({ linkType, linkReference, linkSlug, linkExternal })

  return mimic ? (
    <span className={classes}>
      <ButtonInner title={title} />
    </span>
  ) : onClick ? (
    <button
      onClick={onClick}
      className={classes}
    >
      <ButtonInner title={title} />
    </button>
  ) : link && regex.test(link) ? (
    <a
      href={link}
      className={classes}
    >
      <ButtonInner title={title} />
    </a>
  ) : link ? (
    <Link
      to={link}
      className={classes}
    >
      <ButtonInner title={title} />
    </Link>
  ) : null
}

export default Button
